/**
 * Cart Store - Zustand store for order/cart management
 * 
 * Persists cart to localStorage so items survive page refresh.
 */
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { toast } from 'sonner'

export interface CartItem {
  offerId: string
  name: string
  vendorName: string | null
  distributorCode: string
  billingCycle: string | null
  quantity: number
  minQuantity: number
  maxQuantity: number | null
}

interface CartState {
  items: CartItem[]
  isOpen: boolean

  // Actions
  addItem: (item: Omit<CartItem, 'quantity'> & { quantity?: number }) => void
  removeItem: (offerId: string) => void
  updateQuantity: (offerId: string, quantity: number) => void
  clearCart: () => void
  toggleCart: () => void
  setCartOpen: (open: boolean) => void
}

export const useCartStore = create<CartState>()(
  persist(
    (set) => ({
      items: [],
      isOpen: false,

      addItem: (item) =>
        set((state) => {
          const existing = state.items.find((i) => i.offerId === item.offerId)
          if (existing) {
            const newQty = existing.quantity + (item.quantity ?? 1)
            const max = existing.maxQuantity
            toast.success('Updated quantity', { description: item.name })
            return {
              items: state.items.map((i) =>
                i.offerId === item.offerId
                  ? { ...i, quantity: max ? Math.min(newQty, max) : newQty }
                  : i
              ),
              isOpen: true,
            }
          }
          toast.success('Added to cart', { description: item.name })
          return {
            items: [
              ...state.items,
              { ...item, quantity: item.quantity ?? (item.minQuantity || 1) },
            ],
            isOpen: true,
          }
        }),

      removeItem: (offerId) =>
        set((state) => ({
          items: state.items.filter((i) => i.offerId !== offerId),
        })),

      updateQuantity: (offerId, quantity) =>
        set((state) => ({
          items: state.items.map((i) =>
            i.offerId === offerId ? { ...i, quantity: Math.max(i.minQuantity || 1, quantity) } : i
          ),
        })),

      clearCart: () => {
        toast.info('Cart cleared')
        return set({ items: [], isOpen: false })
      },
      toggleCart: () => set((state) => ({ isOpen: !state.isOpen })),
      setCartOpen: (open) => set({ isOpen: open }),
    }),
    {
      name: 'mzanzi-cart',
      partialize: (state) => ({ items: state.items }),
    }
  )
)
